import type { DiceResolvableSourceCustomData } from '@dicetechnology/dice-doris';
import { DICE_RESOLVABLE_SOURCE } from '@dicetechnology/dice-doris';
import { LoggerMessageLevels } from '@dicetechnology/dice-unity/lib/services/Logger/types';
import { hasKeyOfType } from '@dicetechnology/doris-core';
import type { CastLoadInterceptor } from '@dicetechnology/doris-plugin-chromecast-receiver';
import type { cast as castNs } from 'chromecast-caf-receiver';

import { LoggerEventNames } from '../enums';
import { CompatibilityManager, DiceReceiverManager } from '../services';
import type { DiceCustomData } from '../types';

import { configureConvivaPlugin } from './configureConvivaPlugin';
import { configureDatazoomPlugin } from './configureDatazoomPlugin';
import { configureGooglePalPlugin } from './configureGooglePalPlugin';
import { configureLimitSeekableRangePlugin } from './configureLimitSeekableRangePlugin';
import { configureSubtitlePolicyPlugin } from './configureSubtitlePolicyPlugin';
import { configureUserPreferencesPlugin } from './configureUserPreferencesPlugin';
import { configureWatchContext } from './configureWatchContext';
import { configureYospaceSsaiPlugin } from './configureYospaceSsaiPlugin';
import { enrichMediaMetadata } from './enrichMediaMetadata';
import { getBeaconPayloadFromChromecastSession } from './getBeaconPayloadFromChromecastSession';
import { getChromecastSession } from './getChromecastSession';
import { getConvivaMetadata } from './getConvivaMetadata';
import { getErrorPayload } from './getErrorPayload';
import { getMuxMetadata } from './getMuxMetadata';
import { liveSeekableRangePatch } from './liveSeekableRangePatch';
import { prefetchDiceStream } from './prefetchDiceStream';

declare const cast: typeof castNs;

export const loadInterceptor: CastLoadInterceptor = async (loadRequest, { player }) => {
    const { log } = player.getServices();
    const logger = log.createLogger('loadInterceptor');

    const { media } = loadRequest;
    const mediaCustomData: DiceCustomData | undefined = media.customData;

    const loadRequestCustomData: unknown = loadRequest.customData;
    if (loadRequestCustomData && hasKeyOfType(loadRequestCustomData, 'supportedReceiverVersion', 'string')) {
        const { supportedReceiverVersion } = loadRequestCustomData;
        logger.info(`Setting supportedReceiverVersion to '${supportedReceiverVersion}'`);
        CompatibilityManager.getInstance().setSenderVersion(supportedReceiverVersion);
    }

    const diceReceiverManager = DiceReceiverManager.getInstance();

    if (!mediaCustomData?.chromecastSessionSerialized && !mediaCustomData?.chromecastSession) {
        diceReceiverManager.sendLog({
            eventName: LoggerEventNames.MEDIA_LOAD_INVALID,
            level: LoggerMessageLevels.ERROR,
            message: 'Invalid media payload.'
        });
        throw new Error('Invalid media metadata');
    }

    try {
        // Patch for mobile apps while the liveSeekableRange issue is being fixed.
        // TODO: Remove this 2 month after the release of the fix to stop DVR window drift / de-sync between sender and receiver.
        liveSeekableRangePatch.isEnabled = 'eventInfoSerialized' in mediaCustomData && !mediaCustomData.bypassLiveSeekableRangePatch;

        const chromecastSession = getChromecastSession(mediaCustomData);
        const beaconPayload = getBeaconPayloadFromChromecastSession(chromecastSession, media, loadRequest.currentTime);

        // Configure services for the incoming session
        await diceReceiverManager.configure(chromecastSession, beaconPayload);

        const pluginConfigurations: Promise<void>[] = [];

        diceReceiverManager.getCustomMessageListener().configureCustomMessageListener(player);

        // Configure plugins
        pluginConfigurations.push(configureSubtitlePolicyPlugin(player));
        pluginConfigurations.push(configureLimitSeekableRangePlugin(player, loadRequest));
        pluginConfigurations.push(configureUserPreferencesPlugin(player));
        pluginConfigurations.push(configureConvivaPlugin(player));
        pluginConfigurations.push(configureGooglePalPlugin(player));

        // Configure custom data for plugins
        const muxMetadata = await getMuxMetadata(loadRequest);
        const convivaMetadata = await getConvivaMetadata(loadRequest);

        media.customData = {
            ...media.customData,
            ...muxMetadata,
            ...convivaMetadata
        };

        if (media.customData?.resolutionType === DICE_RESOLVABLE_SOURCE && media.customData?.resolutionCustomData) {
            const resolutionCustomData: DiceResolvableSourceCustomData = media.customData.resolutionCustomData;

            const isLive = !!resolutionCustomData.isLive;

            const withV5 = diceReceiverManager.getWillUseV5Endpoint(isLive);

            // Configure optional advertising plugins
            pluginConfigurations.push(configureDatazoomPlugin(player, withV5));
            pluginConfigurations.push(configureYospaceSsaiPlugin(player, withV5));

            // Wait for all plugins to load before prefetching the stream
            await Promise.all(pluginConfigurations);

            logger.log(`Prefetching Dice stream info (Endpoint version: ${withV5 ? 'V5' : 'V4'})...`);
            const streamInfoOrStreamData = await prefetchDiceStream(media.contentId, resolutionCustomData);
            logger.log(`Stream ${withV5 ? 'data' : 'info'}:`, streamInfoOrStreamData);

            // If the load request contains side-loaded text tracks, we'll disable resolving
            // these via resolvable sources (unless they are explicitly enabled).
            if (media.tracks?.some((track) => track.type === cast.framework.messages.TrackType.TEXT)) {
                resolutionCustomData.enableTextTracks ??= false;
            }

            // Configure watch context for playlists / watchlists / seasons, and all VODs in M&E realms (for backwards compatibility).
            configureWatchContext({
                enableAutoAdvance: mediaCustomData.enableAutoAdvance,
                isLive,
                startingContentId: media.contentId,
                watchContext: mediaCustomData.watchContext
            });

            // Add missing metadata (title, description, bifs, etc.)
            enrichMediaMetadata({
                media: loadRequest.media,
                streamInfoOrStreamData
            });
        } else {
            await Promise.all(pluginConfigurations);
        }

        return loadRequest;
    } catch (error) {
        const errorPayload = await getErrorPayload(error);
        diceReceiverManager.sendLog({
            eventName: LoggerEventNames.MEDIA_LOAD_FAILED,
            level: LoggerMessageLevels.ERROR,
            message: 'Failed to load media.',
            stackTrace: JSON.stringify(errorPayload)
        });
        throw error;
    }
};
